import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, ChevronDown, Check, Sparkles } from 'lucide-react';
import ChoreEditor from './ChoreEditor';
import CleanTimer from './CleanTimer';
import CleanStreak from './CleanStreak';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/chores`;
const opts = { withCredentials: true };
export const FREQ = {
  day: { label: 'Varje dag', emoji: '☀️', bg: '#FFE3EF', edge: '#FF8FB8', ink: '#9C2A5B' },
  week: { label: 'Varje vecka', emoji: '🫧', bg: '#DDF1FF', edge: '#6CB8F0', ink: '#1F5F8F' },
  month: { label: 'Varje månad', emoji: '🌿', bg: '#DDF7E8', edge: '#5FCB8E', ink: '#1E6B42' },
  quarter: { label: 'Var 3:e–6:e månad', emoji: '🔮', bg: '#EDE4FF', edge: '#A98BF0', ink: '#5132A0' },
  year: { label: 'Varje år', emoji: '🌻', bg: '#FFF4C7', edge: '#F2C230', ink: '#7A5A00' },
};
export const CHANGED = 'focus-critter:chores-changed';
const SHOW = 3;
export const WD = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
const CHEERS = {
  week: 'Hela veckans städning är klar! Nu får hemmet glänsa och du får vila, gumman 🫧💛',
  month: 'Alla månadens sysslor avbockade! Farmor är så stolt, det här firar vi med en kanelbulle 🌿🎉',
  quarter: 'Storstädningen är gjord! Sådant gör man inte i en handvändning, bravo 🔮✨',
  year: 'Årets sysslor är klara! Nu kan du luta dig tillbaka hela vägen till nyår 🌻👏',
};
export const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function leftText(g) {
  if (g.freq === 'day') return 'börjar om imorgon';
  if (g.days_left === 0) return 'sista dagen idag!';
  return g.days_left === 1 ? '1 dag kvar' : `${g.days_left} dagar kvar`;
}

function Chips({ i }) {
  return (
    <>
      {i.weekday != null && <span className={`ml-1 text-[10px] font-black rounded-full px-1.5 bg-white border ${i.today ? 'underline' : 'opacity-70'}`} data-testid={`chore-wd-${i.id}`}>{i.today ? 'idag!' : WD[i.weekday]}</span>}
      {i.kid_name && <span className="ml-1 text-[10px] font-black rounded-full px-1.5 bg-white border" data-testid={`chore-kid-${i.id}`}>🧸 {i.kid_name}</span>}
    </>
  );
}

function ChoreCard({ g, onToggle, onTimer }) {
  const [more, setMore] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [showLater, setShowLater] = useState(false);
  const c = FREQ[g.freq];
  const todo = g.items.filter((i) => !i.done && !i.upcoming);
  const later = g.items.filter((i) => !i.done && i.upcoming);
  const done = g.items.filter((i) => i.done);
  const shown = more ? todo : todo.slice(0, SHOW);
  return (
    <div className="rounded-2xl border-[3px] p-3 transition-transform hover:-rotate-[0.4deg]" style={{ background: c.bg, borderColor: c.edge, color: c.ink }} data-testid={`chores-card-${g.freq}`}>
      <div className="mb-1.5">
        <p className="font-black text-sm flex items-center gap-1.5"><span className="text-lg">{c.emoji}</span>{c.label}</p>
        <span className="inline-block mt-0.5 text-[10px] font-black uppercase rounded-full px-2 py-0.5 bg-white/70" data-testid={`chores-left-${g.freq}`}>
          {g.left ? `${g.left} kvar · ${leftText(g)}` : g.upcoming ? `klart för nu · ${g.upcoming} kommer senare` : 'allt klart ✨'}
        </span>
      </div>
      {g.freq === 'month' && g.left > 0 && g.days_left <= 2 && (
        <p className="text-xs font-bold rounded-lg px-2 py-1.5 mb-1.5 bg-white/90 border-2 border-dashed" style={{ borderColor: c.edge }} data-testid="chores-month-nudge">
          👵🏼 {g.days_left === 0 ? 'Sista dagen av månaden' : `Bara ${g.days_left} ${g.days_left === 1 ? 'dag' : 'dagar'} kvar av månaden`}, och {g.left} {g.left === 1 ? 'syssla' : 'sysslor'} kvar. Ta en liten idag, så är det gjort 💛
        </p>
      )}
      {!todo.length && !later.length && <p className="text-sm font-bold py-1" data-testid={`chores-alldone-${g.freq}`}>Allt är gjort! Farmor är stolt 💛</p>}
      {!todo.length && later.length > 0 && <p className="text-sm font-bold py-1" data-testid={`chores-nothing-now-${g.freq}`}>Inget mer just nu 🌸</p>}
      <ul className="space-y-1">
        {shown.map((i) => (
          <li key={i.id} className="flex items-stretch gap-1">
            <button onClick={() => onToggle(i, true)} className="group flex-1 min-w-0 flex items-start gap-2 text-left text-sm rounded-lg px-2 py-1.5 bg-white/80 hover:bg-white transition-colors" data-testid={`chore-${i.id}`}>
              <span className="mt-0.5 w-4 h-4 flex-shrink-0 rounded-md border-2 bg-white flex items-center justify-center transition-colors group-hover:bg-current" style={{ borderColor: c.edge }} />
              <span className="flex-1">{i.text}<Chips i={i} /></span>
            </button>
            <button onClick={() => onTimer(i)} title="Städa med Elsa – timer" aria-label={`Starta städtimer för ${i.text}`}
              className="flex-shrink-0 w-8 rounded-lg bg-white/80 hover:bg-white text-xs transition-colors" data-testid={`chore-timer-${i.id}`}>▶</button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-x-3 mt-1.5 text-[11px] font-bold">
        {todo.length > SHOW && (
          <button onClick={() => setMore(!more)} className="flex items-center gap-0.5 underline" data-testid={`chores-more-${g.freq}`}>
            <ChevronDown className={`w-3 h-3 transition-transform ${more ? 'rotate-180' : ''}`} />{more ? 'Visa färre' : `Visa ${todo.length - SHOW} till`}
          </button>
        )}
        {later.length > 0 && (
          <button onClick={() => setShowLater(!showLater)} className="flex items-center gap-0.5 opacity-80 underline" data-testid={`chores-later-toggle-${g.freq}`}>
            📅 {later.length} senare i veckan
          </button>
        )}
        {done.length > 0 && (
          <button onClick={() => setShowDone(!showDone)} className="flex items-center gap-0.5 opacity-80 underline" data-testid={`chores-done-toggle-${g.freq}`}>
            <Check className="w-3 h-3" />{done.length} klara
          </button>
        )}
      </div>
      {showLater && (
        <ul className="mt-1 space-y-0.5 text-xs" data-testid={`chores-later-${g.freq}`}>
          {later.map((i) => <li key={i.id} className="opacity-75">{WD[i.weekday]}: {i.text}{i.kid_name ? ` (${i.kid_name})` : ''}</li>)}
        </ul>
      )}
      {showDone && (
        <ul className="mt-1 space-y-0.5" data-testid={`chores-done-${g.freq}`}>
          {done.map((i) => (
            <li key={i.id}>
              <button onClick={() => onToggle(i, false)} className="text-xs line-through opacity-60 hover:opacity-100" title="Ångra" data-testid={`chore-undo-${i.id}`}>✓ {i.text}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ChoreBoard({ date, freqs = Object.keys(FREQ), initial, title = 'Städa vad? När?', onHide }) {
  const [data, setData] = useState(initial || null);
  const [editing, setEditing] = useState(false);
  const [timerChore, setTimerChore] = useState(null);
  const closeTimer = useCallback(() => setTimerChore(null), []);
  const [srcId] = useState(() => Math.random().toString(36).slice(2));
  const load = useCallback(() => axios.get(API, { ...opts, params: { date } }).then((r) => setData(r.data)).catch(() => {}), [date]);
  useEffect(() => { if (!initial) load(); }, [load, initial]);

  const toggle = async (item, done) => {
    const g = data.groups.find((x) => x.items.some((i) => i.id === item.id));
    setData((d) => ({ ...d, groups: d.groups.map((x) => (x.freq !== g.freq ? x : { ...x,
      items: x.items.map((i) => (i.id === item.id ? { ...i, done } : i)),
      left: x.left + (item.upcoming ? 0 : done ? -1 : 1) })) }));
    if (done && CHEERS[g.freq] && g.items.every((i) => i.done || i.id === item.id)) {
      window.dispatchEvent(new CustomEvent('focus-critter:day-done', { detail: { line: CHEERS[g.freq] } }));
    } else if (done) toast.success(`${item.text} – klart! ✨`);
    try {
      const r = await axios.post(`${API}/${item.id}/toggle`, { date, done }, opts);
      window.dispatchEvent(new CustomEvent(CHANGED, { detail: { id: item.id, done, sticker: r.data.sticker, src: srcId } }));
      if (r.data.sticker && !r.data.sticker.removed) {
        toast.success(`${r.data.sticker.emoji} ${r.data.sticker.kid_name} fick ett klistermärke!`);
        window.dispatchEvent(new Event('focus-critter:rewards-changed'));
      }
    } catch { toast.error('Kunde inte spara'); load(); }
  };
  useEffect(() => {
    const on = (e) => {
      if (e.detail.src === srcId) return;
      setData((d) => d && ({ ...d, groups: d.groups.map((x) => {
        const it = x.items.find((i) => i.id === e.detail.id);
        if (!it || it.done === e.detail.done) return x;
        return { ...x, items: x.items.map((i) => (i.id === it.id ? { ...i, done: e.detail.done } : i)), left: x.left + (it.upcoming ? 0 : e.detail.done ? -1 : 1) };
      }) }));
    };
    window.addEventListener(CHANGED, on);
    return () => window.removeEventListener(CHANGED, on);
  }, [srcId]);

  if (!data) return null;
  const groups = data.groups.filter((g) => freqs.includes(g.freq) && g.items.length);
  return (
    <section className="space-y-2" data-testid={`chores-board${freqs.length < 5 ? `-${freqs.join('-')}` : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-black flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#FF8FB8]" />{title}</p>
        <CleanStreak />
        <div className="flex items-center gap-3 text-[11px] font-bold">
          <button onClick={() => setEditing(true)} className="flex items-center gap-1 underline opacity-70 hover:opacity-100" data-testid="chores-edit-btn"><Pencil className="w-3 h-3" />Ändra listan</button>
          {onHide && <button onClick={onHide} className="underline opacity-60 hover:opacity-100" data-testid="chores-hide">dölj</button>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {groups.map((g) => <ChoreCard key={g.freq} g={g} onToggle={toggle} onTimer={setTimerChore} />)}
      </div>
      {editing && <ChoreEditor onClose={() => setEditing(false)} onSaved={load} />}
      {timerChore && <CleanTimer chore={timerChore} onDone={(i) => toggle(i, true)} onClose={closeTimer} />}
    </section>
  );
}
